<template>
  <div class="login-page">
    <!-- Animated SVG Background Section -->
    <div class="visual-section">
      <svg class="animated-visual" viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
        <defs>
          <!-- Blueprint grid pattern -->
          <pattern id="gridPattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0, 122, 204, 0.15)" stroke-width="0.5"/>
          </pattern>
          
          <!-- Fine detail grid -->
          <pattern id="fineGrid" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="5" cy="5" r="0.5" fill="rgba(0, 122, 204, 0.08)"/>
          </pattern>
          
          <!-- Morphing paths for particles -->
          <path id="path1" d="M 520,300 A 220,220 0 1,1 80,300 A 220,220 0 1,1 520,300 Z" fill="none">
            <animate attributeName="d"
              values="M 530,300 A 230,210 0 1,1 70,300 A 230,210 0 1,1 530,300 Z;
                      M 540,300 A 240,200 0 1,1 60,300 A 240,200 0 1,1 540,300 Z;
                      M 530,300 A 230,210 0 1,1 70,300 A 230,210 0 1,1 530,300 Z"
              dur="8s" repeatCount="indefinite" />
          </path>
          
          <path id="path2" d="M 480,300 A 180,180 0 1,1 120,300 A 180,180 0 1,1 480,300 Z" fill="none">
            <animate attributeName="d"
              values="M 490,300 A 190,170 0 1,1 110,300 A 190,170 0 1,1 490,300 Z;
                      M 500,300 A 200,160 0 1,1 100,300 A 200,160 0 1,1 500,300 Z;
                      M 490,300 A 190,170 0 1,1 110,300 A 190,170 0 1,1 490,300 Z"
              dur="7s" repeatCount="indefinite" />
          </path>
          
          <path id="path3" d="M 440,300 A 140,140 0 1,1 160,300 A 140,140 0 1,1 440,300 Z" fill="none">
            <animate attributeName="d"
              values="M 445,300 A 145,135 0 1,1 155,300 A 145,135 0 1,1 445,300 Z;
                      M 450,300 A 150,130 0 1,1 150,300 A 150,130 0 1,1 450,300 Z;
                      M 445,300 A 145,135 0 1,1 155,300 A 145,135 0 1,1 445,300 Z"
              dur="6s" repeatCount="indefinite" />
          </path>
          
          <path id="path4" d="M 400,300 A 100,100 0 1,1 200,300 A 100,100 0 1,1 400,300 Z" fill="none">
            <animate attributeName="d"
              values="M 405,300 A 105,95 0 1,1 195,300 A 105,95 0 1,1 405,300 Z;
                      M 410,300 A 110,90 0 1,1 190,300 A 110,90 0 1,1 410,300 Z;
                      M 405,300 A 105,95 0 1,1 195,300 A 105,95 0 1,1 405,300 Z"
              dur="5s" repeatCount="indefinite" />
          </path>
          
          <path id="path5" d="M 360,300 A 60,60 0 1,1 240,300 A 60,60 0 1,1 360,300 Z" fill="none">
            <animate attributeName="d"
              values="M 365,300 A 65,55 0 1,1 235,300 A 65,55 0 1,1 365,300 Z;
                      M 370,300 A 70,50 0 1,1 230,300 A 70,50 0 1,1 370,300 Z;
                      M 365,300 A 65,55 0 1,1 235,300 A 65,55 0 1,1 365,300 Z"
              dur="4s" repeatCount="indefinite" />
          </path>
        </defs>
        
        <!-- Background grids -->
        <rect width="600" height="600" fill="url(#fineGrid)" />
        <rect width="600" height="600" fill="url(#gridPattern)" />
        
        <!-- Nested Configuration Circles - representing hierarchy with morphing -->
        <g class="config-circles">
          <ellipse class="circle circle-1" cx="300" cy="300" rx="220" ry="220">
            <animate attributeName="rx" values="220;230;220" dur="8s" repeatCount="indefinite" />
            <animate attributeName="ry" values="220;210;220" dur="8s" repeatCount="indefinite" />
          </ellipse>
          <ellipse class="circle circle-2" cx="300" cy="300" rx="180" ry="180">
            <animate attributeName="rx" values="180;190;180" dur="7s" repeatCount="indefinite" />
            <animate attributeName="ry" values="180;170;180" dur="7s" repeatCount="indefinite" />
          </ellipse>
          <ellipse class="circle circle-3" cx="300" cy="300" rx="140" ry="140">
            <animate attributeName="rx" values="140;145;140" dur="6s" repeatCount="indefinite" />
            <animate attributeName="ry" values="140;135;140" dur="6s" repeatCount="indefinite" />
          </ellipse>
          <ellipse class="circle circle-4" cx="300" cy="300" rx="100" ry="100">
            <animate attributeName="rx" values="100;105;100" dur="5s" repeatCount="indefinite" />
            <animate attributeName="ry" values="100;95;100" dur="5s" repeatCount="indefinite" />
          </ellipse>
          <ellipse class="circle circle-5" cx="300" cy="300" rx="60" ry="60">
            <animate attributeName="rx" values="60;65;60" dur="4s" repeatCount="indefinite" />
            <animate attributeName="ry" values="60;55;60" dur="4s" repeatCount="indefinite" />
          </ellipse>
        </g>

        <!-- Data Particles following morphing paths -->
        <g class="flow-particles">
          <!-- Particle on outer path -->
          <circle class="particle particle-1" r="3">
            <animateMotion dur="12s" repeatCount="indefinite">
              <mpath href="#path1"/>
            </animateMotion>
            <animate attributeName="r" values="2;4;2" dur="2s" repeatCount="indefinite" />
          </circle>
          
          <!-- Particle on second path -->
          <circle class="particle particle-2" r="3">
            <animateMotion dur="10s" repeatCount="indefinite" begin="2s">
              <mpath href="#path2"/>
            </animateMotion>
            <animate attributeName="r" values="2;4;2" dur="1.8s" repeatCount="indefinite" />
          </circle>
          
          <!-- Particle on third path -->
          <circle class="particle particle-3" r="2.5">
            <animateMotion dur="8s" repeatCount="indefinite" begin="1s">
              <mpath href="#path3"/>
            </animateMotion>
            <animate attributeName="r" values="1.5;3.5;1.5" dur="1.6s" repeatCount="indefinite" />
          </circle>
          
          <!-- Particle on fourth path -->
          <circle class="particle particle-4" r="2.5">
            <animateMotion dur="7s" repeatCount="indefinite" begin="3s">
              <mpath href="#path4"/>
            </animateMotion>
            <animate attributeName="r" values="1.5;3.5;1.5" dur="1.5s" repeatCount="indefinite" />
          </circle>
          
          <!-- Particle on inner path -->
          <circle class="particle particle-5" r="2">
            <animateMotion dur="5s" repeatCount="indefinite" begin="1.5s">
              <mpath href="#path5"/>
            </animateMotion>
            <animate attributeName="r" values="1;3;1" dur="1.2s" repeatCount="indefinite" />
          </circle>
        </g>

        <!-- Central Processing Node -->
        <g class="central-core">
          <circle cx="300" cy="300" r="35" class="core-outer" />
          <circle cx="300" cy="300" r="25" class="core-middle" />
          <circle cx="300" cy="300" r="15" class="core-inner" />
          
          <!-- Rotating indicator lines inside core -->
          <g class="core-indicators">
            <line x1="300" y1="285" x2="300" y2="275" class="indicator" />
            <line x1="315" y1="300" x2="325" y2="300" class="indicator" />
            <line x1="300" y1="315" x2="300" y2="325" class="indicator" />
            <line x1="285" y1="300" x2="275" y2="300" class="indicator" />
          </g>
        </g>
      </svg>
    </div>

    <!-- Login Form Section -->
    <div class="form-section">
      <div class="login-container">
        <div class="brand">
          <svg class="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 490 108.2" xml:space="preserve">
            <path fill="#384d7a" d="M0 15.7h23.5l37.4 48v-48h24.8v90.8H63.6L24.8 56.7v49.8H0zM127 87.2c2.2 0 4.3-.4 6.1-1.2 1.8-.8 3.3-1.9 4.6-3.3 1.3-1.4 2.2-3.1 2.9-5 .7-1.9 1-3.9 1-5.9v-.3c0-2.1-.4-4.2-1.1-6-.7-1.9-1.8-3.5-3.1-5-1.3-1.4-2.9-2.5-4.7-3.4-1.8-.9-3.8-1.3-6-1.3-2.3 0-4.3.4-6.1 1.2-1.8.8-3.4 1.9-4.6 3.3-1.3 1.4-2.2 3.1-2.9 5-.7 1.9-1 3.9-1 5.9v.3c0 2.1.4 4.2 1.1 6 .7 1.9 1.8 3.5 3.1 4.9 1.3 1.4 2.9 2.6 4.7 3.4 1.9.9 3.9 1.4 6 1.4m-.2 21c-5.6 0-10.8-1-15.5-2.9-4.7-1.9-8.8-4.5-12.3-7.8s-6.1-7.2-8-11.6c-1.9-4.4-2.9-9.1-2.9-14.1v-.3c0-5 1-9.7 2.9-14.2s4.6-8.3 8.1-11.7c3.5-3.3 7.6-6 12.3-7.9 4.8-1.9 9.9-2.9 15.6-2.9 5.6 0 10.8 1 15.5 2.9 4.7 1.9 8.8 4.6 12.3 7.8 3.5 3.3 6.1 7.1 8 11.5 1.9 4.4 2.9 9.1 2.9 14.1v.3c0 5-1 9.7-2.9 14.2s-4.6 8.3-8.1 11.7c-3.5 3.3-7.6 6-12.3 7.9-4.8 2-10 3-15.6 3M215.1 65.6c-.4-3.9-1.7-7-3.9-9.3-2.2-2.3-4.9-3.4-8.3-3.4-3.3 0-6 1.1-8 3.4-2.1 2.3-3.5 5.4-4.2 9.3h24.4zM205 108.2c-5.5 0-10.6-.9-15.2-2.7-4.6-1.8-8.5-4.2-11.9-7.4-3.3-3.2-5.9-7-7.8-11.5-1.9-4.5-2.8-9.5-2.8-14.9v-.3c0-5.1.9-9.9 2.6-14.3 1.7-4.5 4.2-8.3 7.3-11.7 3.2-3.3 6.9-5.9 11.2-7.8 4.3-1.9 9.1-2.9 14.3-2.9 6.1 0 11.3 1 15.8 3.1s8.1 4.9 11 8.4c2.9 3.5 5.1 7.6 6.5 12.2 1.4 4.6 2.1 9.5 2.1 14.7v2.5c0 .9 0 1.7-.1 2.6h-46.9c.9 3.9 2.7 6.8 5.4 8.7 2.6 1.9 5.9 2.9 9.8 2.9 2.9 0 5.6-.6 8.1-1.7s4.9-2.9 7.5-5.4L235.5 94c-3.4 4.3-7.5 7.8-12.5 10.4-5 2.5-11 3.8-18 3.8M269.2 107.9c-7.5 0-13.3-1.8-17.5-5.3-4.2-3.5-6.3-9.5-6.3-17.9V56.2h-8.3V36.3h8.3V18.6h24.8v17.8h16.3v19.8h-16.3v23.7c0 2.5.6 4.3 1.7 5.4 1.1 1.1 2.8 1.6 5.1 1.6 1.6 0 3.2-.2 4.8-.6 1.6-.4 3.1-1 4.5-1.7v19.3c-2.3 1.2-4.8 2.2-7.7 2.9-2.8.7-6 1.1-9.4 1.1M289.9 36.3h24.8v70.2h-24.8z M353.1 108.2c-5.3 0-10.2-1-14.7-2.9s-8.4-4.6-11.6-7.8c-3.2-3.3-5.8-7.2-7.7-11.6-1.9-4.5-2.8-9.1-2.8-14.1v-.3c0-4.9.9-9.6 2.8-14.1s4.4-8.3 7.7-11.7c3.3-3.3 7.2-6 11.9-7.9 4.6-1.9 9.7-2.9 15.2-2.9 7.3 0 13.3 1.4 18.2 4.3 4.8 2.9 8.8 6.7 11.8 11.4L367.2 63c-1.7-2.3-3.6-4.2-5.6-5.4-2-1.3-4.5-1.9-7.5-1.9-2 0-3.8.4-5.5 1.2-1.7.8-3.1 1.9-4.3 3.4-1.2 1.4-2.2 3.1-2.9 4.9-.7 1.9-1 3.9-1 6v.3c0 2.2.3 4.3 1 6.3.7 1.9 1.6 3.6 2.9 5.1 1.2 1.4 2.7 2.5 4.4 3.4 1.7.8 3.6 1.2 5.7 1.2 2.9 0 5.4-.6 7.5-1.9 2-1.3 4-3.1 6-5.3l16.5 11.9c-3.3 4.8-7.4 8.7-12.3 11.7-5 2.8-11.3 4.3-19 4.3M418.2 92.6c3.6 0 6.6-1 8.8-3.1 2.2-2.1 3.4-4.9 3.4-8.4v-3.2c-1.2-.5-2.7-.9-4.3-1.3-1.7-.3-3.4-.5-5.3-.5-3.6 0-6.4.8-8.4 2.4-1.9 1.6-2.9 3.7-2.9 6.3v.2c0 2.4.8 4.3 2.5 5.6 1.6 1.3 3.7 2 6.2 2m-8.8 15.4c-3.4 0-6.5-.5-9.3-1.4-2.9-.9-5.3-2.3-7.5-4.1-2.1-1.8-3.8-4.1-5-6.7-1.2-2.7-1.8-5.8-1.8-9.3v-.3c0-3.6.7-6.8 2-9.6s3.2-5.1 5.6-6.9c2.4-1.8 5.3-3.2 8.6-4.1 3.4-1 7.1-1.4 11.3-1.4 3.3 0 6.4.3 9.4.8 3 .6 5.5 1.2 7.5 1.9v-1.2c0-3.6-1.1-6.4-3.4-8.4s-5.8-3-10.6-3c-3.7 0-7.1.3-10.1 1s-6.1 1.6-9.2 2.9l-5.1-17.4c4-1.6 8.2-3 12.6-4s9.7-1.5 15.8-1.5c6.3 0 11.6.7 16 2.2 4.4 1.5 7.9 3.6 10.7 6.4 2.7 2.6 4.6 5.7 5.8 9.4 1.2 3.7 1.8 8 1.8 12.9v40.3H430v-7.3c-2.4 2.7-5.3 4.8-8.6 6.4-3.4 1.6-7.4 2.4-12 2.4 M490 92c0-8.9-7.2-16.2-16.2-16.2S457.6 83 457.6 92c0 8.9 7.2 16.2 16.2 16.2S490 100.9 490 92"></path>
            <path fill="#fe5a02" d="M318.4 16.2c0-8.9-7.2-16.2-16.2-16.2-8.9 0-16.2 7.2-16.2 16.2 0 8.9 7.2 16.2 16.2 16.2s16.2-7.3 16.2-16.2"></path>
          </svg>
          <p class="subtitle">Configuration Management</p>
        </div>

        <div v-if="authStore.isLoading || authStore.isLoadingPermissions" class="loading-state">
          <div class="spinner"></div>
          <p v-if="authStore.account && !authStore.permissionsVerified">Verifying permissions...</p>
          <p v-else>Authenticating...</p>
        </div>

        <div v-else-if="!authStore.isAuthenticated" class="login-form">
          <button @click="handleLoginPopup" class="btn-login btn-primary">
            Sign in with Entra (Popup)
          </button>
          <button @click="handleLoginRedirect" class="btn-login btn-primary">
            Sign in with Entra (Redirect)
          </button>

          <div v-if="authStore.error" class="error-message">
            {{ authStore.error }}
          </div>

          <!-- <div class="config-info">
            <dl>
              <dt>Client ID:</dt>
              <dd>b94a72c3-966e-40b1-a4f3-ba03f9fd2d10</dd>
              <dt>Tenant ID:</dt>
              <dd>822ef784-771f-4257-9898-e94f265e7eb6</dd>
              <dt>Redirect URI:</dt>
              <dd>http://localhost:8080</dd>
              <dt>Scopes:</dt>
              <dd>User.Read</dd>
            </dl>
          </div> -->
        </div>

        <div v-else class="authenticated-state">
          <div class="welcome">
            <h2>Welcome back,</h2>
            <p class="user-name">{{ authStore.userInfo?.name || 'User' }}</p>
          </div>
          
          <div class="user-info">
            <div class="info-item">
              <span class="label">Email</span>
              <span class="value">{{ authStore.userInfo?.email || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Tenant</span>
              <span class="value">{{ authStore.userInfo?.tenantId || 'N/A' }}</span>
            </div>
          </div>

          <div class="actions">
            <button @click="handleLogout" class="btn-login btn-secondary">
              SIGN OUT
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

onMounted(async () => {
  await authStore.initialize();
  
  // If already authenticated (account + permissions verified), redirect to editor or the intended route
  if (authStore.isAuthenticated) {
    console.log('[Login] Already authenticated, redirecting...');
    const redirect = route.query.redirect as string;
    router.push(redirect || '/editor');
  }
});

// Watch for full authentication (account + permissions) and redirect
watch(() => authStore.isAuthenticated, (isAuthenticated) => {
  if (isAuthenticated) {
    console.log('[Login] Authentication completed, redirecting to editor');
    const redirect = route.query.redirect as string;
    router.push(redirect || '/editor');
  }
});

const handleLoginPopup = async () => {
  try {
    await authStore.loginPopup();
  } catch (err) {
    console.error('Login failed:', err);
  }
};

const handleLoginRedirect = async () => {
  try {
    await authStore.loginRedirect();
  } catch (err) {
    console.error('Login failed:', err);
  }
};

const handleLogout = async () => {
  try {
    await authStore.logout();
  } catch (err) {
    console.error('Logout failed:', err);
  }
};
</script>

<style scoped lang="scss">
.login-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: linear-gradient(135deg, #1a2332 0%, #384d7a 50%, #384d7a 100%);
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
}

// Animated Visual Section
.visual-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 968px) {
    display: none;
  }
}

.animated-visual {
  width: 100%;
  max-width: 600px;
  height: auto;
  filter: drop-shadow(0 0 30px rgba(0, 122, 204, 0.2));
}

// Nested Configuration Circles
.config-circles {
  .circle {
    fill: none;
    stroke: rgba(0, 122, 204, 0.4);
    stroke-width: 1.5;
    animation: circlePulse 10s ease-in-out infinite;
    
    @for $i from 1 through 5 {
      &.circle-#{$i} {
        animation-delay: #{$i * 0.5}s;
      }
    }
  }
}

@keyframes circlePulse {
  0%, 100% {
    opacity: 0.3;
    stroke-width: 1.5;
  }
  50% {
    opacity: 0.6;
    stroke-width: 2;
  }
}

// Flow Particles
.flow-particles {
  .particle {
    fill: rgba(0, 122, 204, 0.95);
    filter: blur(0.5px);
    
    &.particle-1 {
      opacity: 0.8;
    }
    &.particle-2 {
      opacity: 0.85;
    }
    &.particle-3 {
      opacity: 0.9;
    }
    &.particle-4 {
      opacity: 0.95;
    }
    &.particle-5 {
      opacity: 1;
    }
  }
}

// Validation Grid Animation
.validation-grid {
  .hex {
    fill: none;
    stroke: rgba(255, 255, 255, 0.4);
    stroke-width: 2;
    animation: hexGlow 10s ease-in-out infinite;
    
    @for $i from 1 through 7 {
      &.hex-#{$i} {
        animation-delay: #{$i * 0.8}s;
      }
    }
  }
}

@keyframes hexGlow {
  0%, 100% {
    opacity: 0.15;
    stroke: rgba(255, 255, 255, 0.4);
  }
  50% {
    opacity: 0.5;
    stroke: rgba(255, 255, 255, 0.8);
  }
}

// Connection Lines Animation
.connection-lines {
  .conn {
    stroke: rgba(255, 255, 255, 0.6);
    stroke-dasharray: 5, 5;
    animation: dashFlow 4s linear infinite;
    
    @for $i from 1 through 4 {
      &.conn-#{$i} {
        animation-delay: #{$i * 0.5}s;
      }
    }
  }
}

@keyframes dashFlow {
  0% {
    stroke-dashoffset: 0;
    opacity: 0.3;
  }
  100% {
    stroke-dashoffset: 20;
    opacity: 0.7;
  }
}

// Central Core Animation
.central-core {
  .core-outer {
    fill: none;
    stroke: rgba(0, 122, 204, 0.5);
    stroke-width: 2;
    animation: coreOuterPulse 5s ease-in-out infinite;
  }
  
  .core-middle {
    fill: rgba(0, 122, 204, 0.15);
    stroke: rgba(0, 122, 204, 0.3);
    stroke-width: 1;
    animation: coreMiddlePulse 4s ease-in-out infinite;
  }
  
  .core-inner {
    fill: rgba(0, 122, 204, 0.8);
    animation: coreInnerGlow 3s ease-in-out infinite;
  }
  
  .core-indicators {
    animation: indicatorRotate 8s linear infinite;
    transform-origin: 300px 300px;
    
    .indicator {
      stroke: rgba(0, 122, 204, 0.9);
      stroke-width: 2;
      stroke-linecap: round;
    }
  }
}

@keyframes coreOuterPulse {
  0%, 100% {
    opacity: 0.4;
    stroke-width: 2;
  }
  50% {
    opacity: 0.7;
    stroke-width: 2.5;
  }
}

@keyframes coreMiddlePulse {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.05);
  }
}

@keyframes coreInnerGlow {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

@keyframes indicatorRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Form Section
.form-section {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 3rem;
  position: relative;
  
  @media (max-width: 968px) {
    padding: 2rem 1.5rem;
  }
}

.login-container {
  width: 100%;
  max-width: 420px;
}

// Brand
.brand {
  margin-bottom: 3rem;
  
  .logo {
    width: 200px;
    height: auto;
    margin-bottom: 0.75rem;
    color: #1a1a1a;
    display: block;
    
    @media (max-width: 968px) {
      width: 160px;
    }
  }
  
  .subtitle {
    font-size: 0.95rem;
    color: #666;
    margin: 0;
    font-weight: 500;
    letter-spacing: 0.5px;
  }
}

// Loading State
.loading-state {
  text-align: center;
  padding: 3rem 0;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top: 4px solid var(--vscode-focusBorder);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1.5rem;
  }
  
  p {
    color: #666;
    font-size: 0.95rem;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Login Form
.login-form {
  display: grid;
  gap: 8px;

  .btn-login {
    width: max-content;
    padding: 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 1px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    // text-transform: uppercase;
    
    &.btn-primary {
      background: var(--vscode-button-background);
      color: var(--vscode-button-foreground);
      box-shadow: 0 4px 12px rgba(0, 122, 204, 0.3);
      
      &:hover {
        background: var(--vscode-button-hoverBackground);
        box-shadow: 0 6px 16px rgba(0, 122, 204, 0.4);
        transform: translateY(-2px);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
  }
  
  .error-message {
    margin-top: 1.5rem;
    padding: 1rem;
    background: #fee;
    border: 1px solid #fcc;
    border-radius: 6px;
    color: #c33;
    font-size: 0.9rem;
  }
  
  .config-info {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 1px solid #e5e5e5;
    
    dl {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.75rem 1rem;
      margin: 0;
      
      dt {
        font-size: 0.8rem;
        color: #999;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      dd {
        font-size: 0.85rem;
        color: #666;
        margin: 0;
        word-break: break-all;
        font-family: 'Courier New', monospace;
      }
    }
  }
}

// Authenticated State
.authenticated-state {
  .welcome {
    margin-bottom: 2rem;
    
    h2 {
      font-size: 1.5rem;
      color: #1a1a1a;
      margin: 0 0 0.5rem;
      font-weight: 600;
    }
    
    .user-name {
      font-size: 1.25rem;
      color: var(--vscode-focusBorder);
      font-weight: 600;
      margin: 0;
    }
  }
  
  .user-info {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    
    .info-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-bottom: 1rem;
      
      &:last-child {
        margin-bottom: 0;
      }
      
      .label {
        font-size: 0.75rem;
        color: #999;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 500;
      }
      
      .value {
        font-size: 0.9rem;
        color: #1a1a1a;
        word-break: break-all;
      }
    }
  }
  
  .actions {
    .btn-login {
      width: 100%;
      padding: 1rem;
      font-size: 0.95rem;
      font-weight: 600;
      letter-spacing: 1px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.3s ease;
      text-transform: uppercase;
      
      &.btn-secondary {
        background: #f8f9fa;
        color: #666;
        border: 2px solid #e5e5e5;
        
        &:hover {
          background: #e9ecef;
          border-color: #d0d0d0;
          transform: translateY(-2px);
        }
        
        &:active {
          transform: translateY(0);
        }
      }
    }
  }
}
</style>
